import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import store from '@Store';
import isUndefined from 'lodash/isUndefined';
import pick from 'lodash/pick';
import some from 'lodash/some';
import fromPairs from 'lodash/fromPairs';
import find from 'lodash/find';
import get from 'lodash/get';
import FactsContext from '@Components/Results/substances/contexts/facts.context';
import { SearchTypes } from '@Constants/CommonConstants';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import StructureModel from '@Components/Results/structure/structure-model.service';
import StructureZoomService from '@Services/StructureZoom';
import SubstanceIdentificationInformation from '@Components/Results/substances/information/SubstanceIdentificationInformation';
import SubstanceMoreNamesInformation from '@Components/Results/substances/information/SubstanceMoreNamesInformation';
import SubstanceSupplierInformation from '@Components/Results/substances/information/SubstanceSupplierInformation';
import Structure from '@Components/Results/structure/Structure';

import SubstanceHelper from '@Components/Results/services/substance-helper.factory';
import SubstanceDetailsContainer from '@Components/Results/substances/details/SubstanceDetailsContainer';
import SubstanceAlternativeDetails from '@Components/Results/substances/details/substance-alternative-details.component';
import SubstanceFactLinks from '@Components/Results/substances/details/SubstanceFactLinks';
import SubstanceRightHandLinks from '@Components/Results/substances/details/SubstanceRightHandLinks';
import SubstancePubChemReferences from '@Components/Results/substances/details/substance-pubchem-references.component';
import SubstanceHitData from '@Components/Results/substances/information/SubstanceHitData';
import SubstanceHitDataButton from '@Components/Results/substances/information/SubstanceHitDataButton';
import SubstanceInformation from '@Components/Results/substances/information/SubstanceInformation';
import { contextFromMetadata } from '@Components/Results/utils/metadata';
import { getExpandTopSubstanceFactMappingValue, getUIDirectiveValue, UI_DIRECTIVES } from '@Utils/UIDirectivesParameterHelper';
import Catalyst from '@Services/Catalyst';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';

import './Substance.scss';

function prepareFacts(facts, isMarkushSearch, shouldOpenIdentification = false) {
  return (
    facts &&
    fromPairs(
      facts.map((fact) => {
        if (isMarkushSearch && fact.id === 'ID') {
          const markushFact = find(fact.children, { type: 'markush' });
          if (markushFact) {
            markushFact.open = true;
          }
        }
        return [
          fact.id,
          {
            ...fact,
            open: fact.hitData || (fact.id === 'ID' && (isMarkushSearch || shouldOpenIdentification)),
          },
        ];
      })
    )
  );
}

function computeNewState(originalState, header, fact, value) {
  if (header && originalState) return false;
  if (fact?.id) return true;
  return !isUndefined(value) ? value : !originalState;
}

function Substance({ metadata, substance, zoom, searchType, gridView = false, size, anySubstanceHasHitData = false }) {
  const isMarkushSearch = searchType === SearchTypes.MARKUSH_SEARCH;
  const database = SubstanceHelper.getCurrentDatabase(metadata.databaseId || substance.databaseId);
  const shouldUseTabbedFacts = database.isReaxysContent || database.isCIDatabase || database.isCommercialSupplierData;
  const shouldUseAlternativeFacts = !shouldUseTabbedFacts;
  const initialFacts = prepareFacts(substance.facts, isMarkushSearch);

  const [isFactsDataOpen, setIsFactsDataOpen] = useState(isMarkushSearch);
  const [alternativeContent, setAlternativeContent] = useState({});
  const [facts, setFacts] = useState(initialFacts);
  const isMountedRef = useRef(true);
  const scrollTimerRef = useRef(null);

  const zoomClasses = StructureZoomService.getZoomClasses('substances') || [];
  const structureModel = React.useMemo(() => new StructureModel(substance), [substance]);

  const toggleFact = useCallback(
    (id, open, shouldScroll, exclusive = false) => {
      if (!facts) return Promise.resolve(null);

      const originalState = facts[id]?.open;
      const baseFacts = exclusive ? fromPairs(Object.entries(facts).map(([factId, fact]) => [factId, { ...fact, open: false }])) : facts;
      const updatedFacts = {
        ...baseFacts,
        [id]: {
          ...baseFacts[id],
          open: isUndefined(open) ? !originalState : open,
        },
      };

      const currentFact = updatedFacts[id];
      if (!currentFact) {
        Catalyst.trackError({ message: `Can't find fact by the ID, the data is corrupted.`, component: 'Substance' }, 'se-');
        return Promise.resolve(null);
      }

      const isOpen = currentFact.open;
      if (originalState !== isOpen) {
        setFacts(updatedFacts);
      }

      return SubstanceHelper.checkAndRetrieveFactData(currentFact, structureModel, metadata)
        .then(() => {
          if (!isMountedRef.current) return isOpen;

          setFacts((latestFacts) => {
            if (!latestFacts?.[id] || latestFacts[id].open !== isOpen) {
              return latestFacts;
            }

            return {
              ...latestFacts,
              [id]: {
                ...latestFacts[id],
                ...currentFact,
              },
            };
          });

          if (isOpen) {
            Catalyst.trackAmountOfViewedData(
              get(structureModel, 'details.id', ''),
              currentFact,
              currentFact.type,
              currentFact.title,
              store.getState()
            );
          }
          if (shouldScroll) {
            const targetSelector = currentFact.hitData
              ? `#substances-hitdata-${structureModel.index}`
              : `#substance-fact-details-${structureModel.index}`;
            document.querySelector(targetSelector)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return isOpen;
        })
        .catch(Logger.reject);
    },
    [facts, metadata, structureModel]
  );

  const onToggleAlternativeInfo = useCallback(
    (id) => {
      setAlternativeContent((prev) => {
        const nextValue = !prev?.[id];

        if (database.isPubChem && nextValue) {
          return { [id]: true };
        }

        return {
          ...prev,
          [id]: nextValue,
        };
      });
    },
    [database.isPubChem]
  );

  const toggleState = useCallback(
    (flag, value, fact, header, exclusive = false) => {
      const originalState = { ...pick({ isFactsDataOpen }, flag) }[flag];
      const newState = computeNewState(originalState, header, fact, value);

      if (originalState !== newState) {
        setIsFactsDataOpen(newState);
      }

      if (!fact) return;

      if (!fact.open || !isFactsDataOpen) {
        const trackingLabel = metadata.isViewPreview ? `query:preview:${contextFromMetadata(metadata)}` : undefined;
        Catalyst.trackContentLinkClick(fact.title, trackingLabel);
      }

      const id = `accordion-${fact.className}-${structureModel.index}`;
      if (!originalState && fact.open) {
        if (scrollTimerRef.current) {
          clearTimeout(scrollTimerRef.current);
        }

        scrollTimerRef.current = setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          scrollTimerRef.current = null;
        });
      } else {
        toggleFact(fact.id, value, false, exclusive)
          .then((open) => open)
          .catch(Logger.error);
      }
    },
    [isFactsDataOpen, metadata, structureModel, toggleFact]
  );

  const onToggle = useCallback(
    (id, header, exclusive = true) => {
      const { isReaxysContent, isCIDatabase, isCommercialSupplierData } = database;
      const fact = get(facts, id);

      if (get(fact, 'hitData')) {
        if (!fact.open) {
          const trackingLabel = metadata.isViewPreview ? `query:preview:${contextFromMetadata(metadata)}` : undefined;
          Catalyst.trackContentLinkClick(fact.title, trackingLabel);
        }
        toggleFact(fact.id, !fact.open, true, exclusive);
        return;
      }

      if (isReaxysContent || isCIDatabase || isCommercialSupplierData) {
        toggleState('isFactsDataOpen', !fact?.open, fact, header, exclusive);
        return;
      }

      onToggleAlternativeInfo(id);
    },
    [database, facts, metadata, toggleFact, toggleState, onToggleAlternativeInfo]
  );

  const onCloseFact = useCallback(
    (fact) => {
      toggleState('isFactsDataOpen', false, fact);
    },
    [toggleState]
  );

  // autoOpenCategory logic

  useEffect(() => {
    const { isCommercialSupplierData } = database;

    if (Auth.isFeatureEnabled(FEATURES.enableDisplaySupplierNumber) && isCommercialSupplierData && substance.index === '1') {
      const proddFact = get(facts, 'PRODD');
      const hitDataFact = get(facts, 'HIT_DATA');
      if (proddFact && !hitDataFact) {
        onToggle('PRODD');
      }
      return;
    }
    if (metadata.heatmapMode && size === 1) {
      if (facts?.ID) {
        onToggle('ID', undefined, false);
      }
    }

    const expandTopSubstanceFactDirectiveValue =
      substance.index === '1' &&
      metadata.uiDirectives &&
      getUIDirectiveValue(metadata.uiDirectives, UI_DIRECTIVES.EXPAND_TOP_SUBSTANCE_FACT);
    const expandTopSubstanceFactDirectiveAltValue = getExpandTopSubstanceFactMappingValue(expandTopSubstanceFactDirectiveValue);
    if (expandTopSubstanceFactDirectiveValue && some(facts, { id: expandTopSubstanceFactDirectiveValue })) {
      onToggle(expandTopSubstanceFactDirectiveValue);
    } else if (expandTopSubstanceFactDirectiveAltValue && some(facts, { id: expandTopSubstanceFactDirectiveAltValue })) {
      onToggle(expandTopSubstanceFactDirectiveAltValue);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(
    () => () => {
      isMountedRef.current = false;
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
      }
    },
    []
  );

  const toggleSuppliers = useCallback(() => onToggleAlternativeInfo('supplierInformation'), [onToggleAlternativeInfo]);
  const toggleIdentification = useCallback(() => onToggleAlternativeInfo('identification'), [onToggleAlternativeInfo]);
  const toggleMoreNames = useCallback(() => onToggleAlternativeInfo('moreNames'), [onToggleAlternativeInfo]);

  const { identification, supplierInformation, moreNames } = alternativeContent;
  const actualZoom = metadata.isViewPreview ? 0 : zoom;
  const zoomClass = zoomClasses[actualZoom];
  const factsContextValue = useMemo(() => ({ facts, toggle: toggleFact }), [facts, toggleFact]);

  return (
    <div
      className={`substance-content${database.isPubChem ? ' substance-content--pubchem' : ''}${
        database.isCommercialSupplierData ? ' substance-content--commercial' : ''
      }`}>
      <FactsContext.Provider value={factsContextValue}>
        <div className="substance-content__summary">
          <div className="substance-content__summary-main o-els-flex-layout o-els-flex-layout--left o-els-flex-layout--wrap@mobile">
            <div className="rx-reflow-scrollable">
              <div className={`${zoomClass}`}>
                <Structure
                  metadata={metadata}
                  zoom={actualZoom}
                  structure={structureModel}
                  isCard={gridView}
                  showDetails={gridView}
                  gridView={gridView}
                  footer={
                    gridView ? (
                      <SubstanceHitDataButton metadata={metadata} structure={structureModel} showFooter={anySubstanceHasHitData} />
                    ) : undefined
                  }
                />
              </div>
            </div>

            {!gridView && <SubstanceDetailsContainer metadata={metadata} substance={structureModel} database={database} />}
          </div>

          {!gridView && !metadata?.heatmapMode && (
            <SubstanceRightHandLinks
              metadata={metadata}
              substance={structureModel}
              className="substance-header__related-links substance-header__related-links--in-summary"
            />
          )}

          {!gridView && !metadata?.heatmapMode && database.isPubChem && (
            <SubstancePubChemReferences
              references={structureModel.pubchemReferences}
              className="substance-header__pubchem-links substance-header__pubchem-links--in-summary"
            />
          )}

          {!gridView && shouldUseAlternativeFacts && (
            <SubstanceAlternativeDetails
              substance={structureModel}
              toggleSuppliers={toggleSuppliers}
              toggleIdentification={toggleIdentification}
              toggleMoreNames={toggleMoreNames}
              openStates={alternativeContent}
            />
          )}

          {!gridView && shouldUseTabbedFacts && <SubstanceFactLinks substance={structureModel} onToggle={onToggle} />}
        </div>

        {!gridView && identification && (
          <SubstanceIdentificationInformation substance={structureModel} database={database} onToggle={toggleIdentification} />
        )}

        {!gridView && supplierInformation && (
          <SubstanceSupplierInformation substance={structureModel} database={database} onToggle={toggleSuppliers} />
        )}

        {!gridView && moreNames && <SubstanceMoreNamesInformation substance={structureModel} onToggle={toggleMoreNames} />}

        {!gridView && <SubstanceHitData metadata={metadata} substance={structureModel} database={database} />}

        {!gridView && shouldUseTabbedFacts && isFactsDataOpen && (
          <SubstanceInformation
            metadata={metadata}
            facts={facts}
            toggleFact={toggleFact}
            substance={structureModel}
            database={database}
            onCloseFact={onCloseFact}
            zoom={actualZoom}
          />
        )}
      </FactsContext.Provider>
    </div>
  );
}

Substance.propTypes = {
  metadata: MetadataPropTypesShape,
  substance: PropTypes.object.isRequired,
  zoom: PropTypes.number.isRequired,
  searchType: PropTypes.string,
  gridView: PropTypes.bool,
  size: PropTypes.number,
  anySubstanceHasHitData: PropTypes.bool,
};

export default Substance;
